<template>
  <div
    :class="[
      'play-by-play',
      { 'play-by-play--is-expanded': isExpanded }
    ]"
  >
    <div class="play-by-play__header">
      <span class="play-by-play__header-title">
        Play By Play
      </span>
      <span
        class="play-by-play__header-button"
        @click="toggleExpansion"
      >
        <Icon
          :name="toggleIcon"
        />
      </span>
    </div>
    <div
      ref="scrollerElement"
      class="play-by-play__content"
    >
      <div
        v-if="!actions.length && !displayedTennisActions.length"
        class="play-by-play__message"
      >
        Currently, no data available
      </div>
      <PlayByPlayAction
        v-for="action in displayedActions"
        :key="action.id"
        :message="action.message"
        :clock="action.clock"
      />
      <PlayByPlayTennisAction
        v-for="(tennisAction, index) in displayedTennisActions"
        :key="index"
        :action="tennisAction"
        :home-team="event.home"
        :away-team="event.away"
      />
    </div>
    <div
      v-if="showScrollToTop"
      class="play-by-play__top-button"
      @click="scrollToTop"
    >
      <Icon
        name="arrow-up"
      />
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useScroll } from '@vueuse/core';
import {
  take, filter, each, last, reverse,
} from 'lodash';
import { useSquad } from '@/composables';
import { parsePlayByPlayActions, formatPlayByPlayActions } from '@/services/helpers/play-by-play';
import Tennis from '@/services/helpers/tennis';
import Icon from './Icon';
import PlayByPlayAction from './PlayByPlayAction';
import PlayByPlayTennisAction from './PlayByPlayTennisAction';

const MAX_ACTIONS_COLLAPSED = 3;
const MAX_ACTIONS_EXPANDED = 10;
const MAX_TENNIS_ACTIONS_COLLAPSED = 3;

export default {
  components: {
    Icon,
    PlayByPlayAction,
    PlayByPlayTennisAction,
  },
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    const scrollerElement = ref(null);
    const { y: scrollY } = useScroll(scrollerElement);
    const showScrollToTop = computed(() => scrollY.value >= 40);
    const scrollToTop = () => {
      scrollerElement.value.scrollTo({ top: 0 });
    };

    const isExpanded = ref(false);
    const { squad } = useSquad(props.event);
    const actions = computed(() => parsePlayByPlayActions(props.event));
    const recentActions = computed(() => take(actions.value, MAX_ACTIONS_EXPANDED));
    const formattedActions = computed(() => formatPlayByPlayActions(recentActions.value, {
      event: props.event,
      squad: squad.value,
    }));
    const displayedActions = computed(() => take(formattedActions.value, (isExpanded.value ? MAX_ACTIONS_EXPANDED : MAX_ACTIONS_COLLAPSED)));

    const toggleIcon = computed(() => (isExpanded.value ? 'chevron-up' : 'chevron-down'));
    const toggleExpansion = () => {
      isExpanded.value = !isExpanded.value;
    };

    const tennisPeriods = ['FIRST_SET', 'SECOND_SET', 'THIRD_SET', 'FOURTH_SET', 'FIFTH_SET'];
    const availablePeriods = computed(() => filter(tennisPeriods, (period) => !!props.event?.pointsWonPerGame?.[period]));

    const tennisActions = computed(() => {
      const points = [];
      each(availablePeriods.value, (period) => {
        each(props.event.pointsWonPerGame[period], (game, gameNumber) => {
          each(game, (point) => {
            let homeScore = 0;
            let awayScore = 0;
            const lastPoint = last(points);
            if (gameNumber === '13') {
              if (lastPoint && lastPoint.set === period && lastPoint.game === gameNumber) {
                if (point === 'HOME') {
                  homeScore = lastPoint.homeScore + 1;
                  awayScore = lastPoint.awayScore;
                }
                if (point === 'AWAY') {
                  awayScore = lastPoint.awayScore + 1;
                  homeScore = lastPoint.homeScore;
                }
              } else {
                if (point === 'HOME') {
                  homeScore = 1;
                  awayScore = 0;
                }
                if (point === 'AWAY') {
                  homeScore = 0;
                  awayScore = 1;
                }
              }
            } else if (lastPoint && lastPoint.set === period && lastPoint.game === gameNumber) {
              homeScore = Tennis.increaseGameScore(lastPoint, point)?.homeScore;
              awayScore = Tennis.increaseGameScore(lastPoint, point)?.awayScore;
            } else if (point === 'HOME') {
              homeScore = 15;
              awayScore = 0;
            } else {
              homeScore = 0;
              awayScore = 15;
            }
            points.push({
              set: period,
              game: gameNumber,
              homeScore,
              awayScore,
              pointWon: point,
            });
            if (gameNumber === '13' && homeScore >= 7 && awayScore < (awayScore - 1)) {
              points.push({
                set: period,
                game: gameNumber,
                homeScore: 'GAME_WON',
                awayScore: 'GAME_LOST',
                pointWon: point,
              });
            }
            if (gameNumber === '13' && awayScore >= 7 && homeScore < (awayScore - 1)) {
              points.push({
                set: period,
                game: gameNumber,
                homeScore: 'GAME_LOST',
                awayScore: 'GAME_WON',
                pointWon: point,
              });
            }
          });
        });
      });
      return reverse(points);
    });

    const displayedTennisActions = computed(() => take(tennisActions.value, (isExpanded.value ? tennisActions.value?.length : MAX_TENNIS_ACTIONS_COLLAPSED)));

    return {
      scrollerElement,
      showScrollToTop,
      scrollToTop,
      isExpanded,
      squad,
      actions,
      recentActions,
      formattedActions,
      displayedActions,
      toggleIcon,
      toggleExpansion,
      displayedTennisActions,
    };
  },
};
</script>

<style lang="scss">
.play-by-play {
  display: flex;
  flex-direction: column;
  border-radius: var(--radius-medium, 4px);
  border: 1px solid var(--neutral-stroke-default-resting, #F0F0F0);
  background: var(--neutral-bg-default-resting, #FFF);
  min-height: 136px;
  margin-top: 4px;
  position: relative;

  &--is-expanded {
    min-height: 276px;
    max-height: 276px;
    height: 276px;
    // overflow-y: auto;
  }

  &__header {
    display: flex;
    padding: 2px;
    flex-direction: row;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    align-self: stretch;
    border-radius: var(--radius-none, 0px);
    border-bottom: 1px solid var(--neutral-stroke-default-resting, #F0F0F0);
    background: var(--neutral-bg-weak-resting, #FAFAFA);
    overflow: hidden;
    color: var(--neutral-text-medium-resting, #6D6D6D);
    text-overflow: ellipsis;
    font-family: Rubik;
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
    position: relative;

    &-title {
      position: absolute;
      left: 50%;
      transform: translateX(-50%);
    }

    &-button {
      margin-left: auto;
      cursor: pointer;

      .icon svg {
        stroke: #6D6D6D;
        max-width: 12px;
        max-height: 12px;
      }
    }
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow-y: auto;
  }

  &__message {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--neutral-text-default-disabled, #A9A9A9);
    text-align: center;
    font-family: Rubik;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
  }

  &__top-button {
    position: absolute;
    bottom: 28px;
    left: calc(100% - 24px - 16px);
    width: 24px;
    height: 24px;
    border-radius: 100%;
    background-color: #003C3C;
    padding: 7px;
    cursor: pointer;

    .icon svg path {
      fill: #fff;
    }
  }
}
</style>
